import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(timezone);
dayjs.extend(utc);

const mode: string | undefined = import.meta.env.MODE;

// Format file size
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) { return '0 Bytes'; }

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export const formatDateWithTimeZone = (date: string | dayjs.Dayjs, format: string = "DD MMM YYYY", timeZone: string = "UTC"): string => {
  try {
    // Check if the input is a valid dayjs object or a valid date string
    const d = dayjs.isDayjs(date) ? date : dayjs(date);

    // Check for invalid date
    if (!d.isValid()) {
      return "Invalid Date";
    }

    // Convert the date to the specified timezone before formatting
    return d.format(format);
  } catch (error) {
    console.error("Error in formatDate", error);
    return "Invalid Date";
  }
};


export const formatDate = (date: string, format: string = "DD MMM YYYY"): any => {
  try {
    if (!date || !dayjs(date).isValid()) {
      return "Invalid Date";
    }
    return dayjs(date).format(format);
  } catch (error) {
    console.error("Error in formatDate", error);
    return "Invalid Date";
  }
};

// Format date
// export const formatDate = (date: string | Date): string => {
//   const d = new Date(date);
//   return d.toLocaleDateString('en-US', {
//     year: 'numeric',
//     month: 'short',
//     day: 'numeric',
//   });
// };

// Format date with time
export const formatDateTime = (date: string | Date): string => {

  const d = new Date(date);

  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

};

// Generate initials from name
export const getInitials = (name: string): string => {
  return name
    .split(' ')
    .map(word => word.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

// Debounce function
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout;

  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

// Throttle function
export const throttle = <T extends (...args: any[]) => any>(
  func: T,
  limit: number
): ((...args: Parameters<T>) => void) => {
  let inThrottle: boolean;

  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};

// Generate random ID
export const generateId = (): string => {
  return Math.random().toString(36).substr(2, 9);
};

// Validate email
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Validate phone number
export const isValidPhone = (phone: string): boolean => {
  const phoneRegex = /^[+]?[1-9][\d]{0,15}$/;
  return phoneRegex.test(phone.replace(/\s/g, ''));
};

// Get status color
export const getStatusColor = (status: string): string => {
  const colors: { [key: string]: string } = {
    active: 'green',
    inactive: 'red',
    pending: 'orange',
    completed: 'blue',
    draft: '#d9d9d9',
  };

  return colors[status.toLowerCase()] || '#d9d9d9';
};

// Capitalize first letter
export const capitalize = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

// Truncate text
export const truncate = (text: string, length: number): string => {
  if (text.length <= length) { return text; }
  return text.slice(0, length) + '...';
};


export const logHelper = (tag: string, message: string, value: any) => {
  // console.log("" + tag, " ===> " + key, value);
  if (mode === 'development') {
    try {
      console.log('🟢 ' + tag, " =====> ", message, value);
      // console.trace();
    } catch (error) {
      console.log("🟢", message);
    }
  }
};

export const getEventList = (eventListData: any) => {
  if (!eventListData) { return []; }
  try {
    return eventListData.map((event: any) => ({
      label: event.name,
      value: event.name
    }));
  } catch (error) {
    console.error("Error in getEventList", error);
    return [];
  }
};

export const getServiceTypes = (serviceTypesData: any) => {
  if (!serviceTypesData) { return []; }

  try {
    return serviceTypesData.map((serviceType: any) => ({
      label: serviceType,
      value: serviceType
    }));
  } catch (error) {
    return [];
  }

};

export const getCargoTypes = (cargoTypesData: any) => {
  if (!cargoTypesData) { return []; }

  try {
    return cargoTypesData.map((cargoType: any) => ({
      label: cargoType.name || cargoType,
      value: cargoType.id || cargoType
    }));
  } catch (error) {
    return [];
  }
};

export const strYesNoToBool = (str: string): boolean => {
  return str === 'Yes' || str === 'yes';
};

export const booleanToStrYesNo = (bool: boolean): string => {
  return bool ? 'yes' : 'no';
};

export const cleanDataToSave = (data: any) => {
  const cleanedData = Object.fromEntries(
    Object.entries(data).filter(([_, v]) => v !== undefined && v !== "")
  );
  return cleanedData;
};


export const validateIfNotShipment = (formStatus: any) => {

  const withoutShipment = ['IRENA_status', 'events_status', 'service_logistics', 's_i'];
  const value = "validated";

  const allValid = withoutShipment.every((key: any) => formStatus[key] === value);

  return allValid;

}


export const convertToCSV = (data: any) => {
  const csvContent = data.map((row: any) => Object.values(row).join(',')).join('\n');
  return csvContent;
};

export const logout = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  window.location.href = '/login';
}

export const getRoleListDropWithoutFilter = (roleListData: any) => {
  if (!roleListData) { return []; }
  return roleListData.map((role: any) => ({ label: role.name, value: role.key }));
};

export const getRoleListDrop = (roleListData: any) => {
  // if (!roleListData) return [];
  // return roleListData.map((role: any) => ({ label: role.name, value: role.key }));
  if (!roleListData) { return []; }
  const list = [];
  for (const role of roleListData) {
    if (role.key !== 'admin') {
      list.push({ label: role.name, value: role.key });
    }
  }
  return list;
};

export const getRoleListDropAdmin = (roleListData: any) => {
  if (!roleListData) { return []; }
  const list = [];
  for (const role of roleListData) {
    // if (role.key !== 'admin') {
    list.push({ label: role.name, value: role.key });
    // }
  }
  return list;
};

export const getAccessLevelListDrop = (accessLevelListData: any) => {
  if (!accessLevelListData) { return []; }
  return accessLevelListData.map((item: any) => ({ label: item, value: item }));
};

export const getPermissionListDrop = (permissionListData: any) => {
  if (!permissionListData) { return []; }
  return permissionListData.map((item: any) => ({ label: item.name, value: item.id }));
};

export const getKpiListDrop = (kpiListData: any) => {
  if (!kpiListData) { return []; }
  return kpiListData.map((item: any) => ({ label: item.name, value: item.id }));
};

export const getTemplateListDrop = (templateListData: any) => {
  if (!templateListData) { return []; }
  return templateListData.map((item: any) => ({ label: item.name, value: item.id }));
};

export const getIndicatorListDrop = (indicatorListData: any) => {
  if (!indicatorListData) { return []; }
  return indicatorListData.map((item: any) => ({ label: item.indicator_name, value: item.indicator_id }));
};

export const getMasterIndicatorListDrop = (indicatorListData: any) => {
  if (!indicatorListData) { return []; }
  return indicatorListData.map((item: any) => ({ label: item.indicator_name, value: item.id }));
};

export const getUserListDrop = (userListData: any) => {
  if (!userListData) { return []; }
  return [{ label: 'All', value: 'all' }, ...userListData.map((item: any) => ({ label: item.full_name, value: item.full_name }))];
};

export const getUserListDropWithId = (userListData: any) => {
  if (!userListData) { return []; }
  return userListData.map((item: any) => ({ label: item.full_name, value: item.id }));
};

export const errorValueDisplay = (value: any, error: any) => {

  if (value == "-") {
    return "Empty Cell";
  }

  if (error == "Duplicate row in file") {
    return "Entire row";
  }

  if (value === null) { return 'Empty Cell'; }
  if (value === undefined) { return 'undefined'; }
  if (value === '') { return 'empty'; }
  if (value === 'null') { return 'null'; }
  if (value === 'undefined') { return 'undefined'; }
  if (value === 'empty') { return 'empty'; }
  if (value === 'NaN') { return 'NaN'; }
  if (value === 'Infinity') { return 'Infinity'; }
  if (value === '-Infinity') { return '-Infinity'; }

  return JSON.stringify(value);
};


export const handleExport = (viewRef: any, title: string) => {
  if (viewRef.current) {
    viewRef.current.toImageURL('png', 2).then((url: string) => {
      const link = document.createElement('a');
      link.download = `${title || 'chart'}.png`;
      link.href = url;
      link.click();
    });
  }
};


export const lineBreakerForChartTitle = (title: string, chartWidth: number): string | string[] => {

  const maxCharsPerLine = Math.floor(chartWidth * (63 / 400));
  let formattedTitle: string | string[] = title;

  if (title.length > maxCharsPerLine) {
    const words = title.split(' ');
    const midpoint = Math.ceil(words.length / 2);
    const line1 = words.slice(0, midpoint).join(' ');
    const line2 = words.slice(midpoint).join(' ');
    formattedTitle = [line1, line2];
  }


  return formattedTitle;
}


export const getDataCenterList = (dataCenterList: any) => {
  if (!dataCenterList) { return []; }
  return dataCenterList.map((item: any) => ({ label: item, value: item }));
};
